import { reactive } from 'vue'

/**
 * 全局登录引导弹窗状态管理（游客模式）
 * ============================================================
 * 模块级 reactive state + Promise 模式，所有组件 import 后共享同一实例。
 *
 * 触发流程：
 * 1. 游客点击需登录操作按钮 → 后端返回 40101
 * 2. request.ts 响应拦截器调用 `await showLoginGuide()` → 弹窗显示，Promise pending
 * 3. LoginGuidePopup 弹出，用户输入手机号 + 密码登录
 * 4. 登录成功 → onLoginSuccess() → resolve(pendingPromise)
 * 5. request.ts 的 .then() 链触发：service.request(config) 重试原请求，原 caller 拿到重试结果
 * 6. 用户取消（点 X）→ closeLoginGuide() → reject(pendingPromise) → 原 caller catch
 *
 * 区分语义：
 *  - 401 UNAUTHORIZED → token 过期/无效 → request.ts refresh token 流程（不走此弹窗）
 *  - 40101 NEED_LOGIN → 游客未登录写操作 → 此弹窗引导登录
 *
 * 多调用合并：showLoginGuide() 在弹窗已显示时返回同一 pending Promise，
 * 多个 caller 共享同一登录流程（避免重复弹窗）。
 */
interface ShowLoginGuideOptions {
  /**
   * 用户取消登录时留在当前页（默认 false：沿用 401 场景，取消后回首页）。
   * 游客可浏览、仅 CTA 需登录的页面（如测评介绍页）传 true。
   */
  stayOnClose?: boolean
  /**
   * 登录成功后留在当前页，由调用方 await 后自行续跑业务流程（默认 false：登录成功统一跳首页）。
   * 用于「游客点页面主 CTA → 登录 → 继续当前操作」场景（如 AI 红娘介绍页点立即体验）。
   */
  stayAfterLogin?: boolean
}

interface LoginGuideState {
  /** 弹窗是否显示 */
  visible: boolean
  /** 取消登录时是否留在当前页（每次 showLoginGuide 时重置） */
  stayOnClose: boolean
  /** 登录成功后是否留在当前页续跑调用方流程（每次 showLoginGuide 时重置） */
  stayAfterLogin: boolean
}

const state = reactive<LoginGuideState>({
  visible: false,
  stayOnClose: false,
  stayAfterLogin: false
})

// pending Promise 的 resolve / reject 句柄
let pendingPromise: Promise<void> | null = null
let pendingResolve: (() => void) | null = null
let pendingReject: ((e: any) => void) | null = null

/**
 * 显示登录引导弹窗
 * @returns Promise<void> 登录成功时 resolve；用户取消时 reject(new Error('用户取消登录'))
 *
 * 在 request.ts 中典型用法：
 *   try {
 *     await showLoginGuide()
 *     return service.request(config)  // 重试原请求
 *   } catch (_e) { return Promise.reject(res) }
 *
 * 在按钮点击中典型用法：
 *   try {
 *     await showLoginGuide()
 *     router.push('/mine')  // 登录后跳转
 *   } catch (_e) { /* 用户取消 *\/ }
 */
export function showLoginGuide(options: ShowLoginGuideOptions = {}): Promise<void> {
  // 弹窗已显示：复用同一 pending Promise，避免重复弹窗 / 多 caller 互不感知
  if (state.visible && pendingPromise) {
    return pendingPromise
  }

  state.stayOnClose = options.stayOnClose === true
  state.stayAfterLogin = options.stayAfterLogin === true
  state.visible = true
  pendingPromise = new Promise<void>((resolve, reject) => {
    pendingResolve = resolve
    pendingReject = reject
  })
  return pendingPromise
}

/**
 * 关闭登录引导弹窗（用户主动取消）
 * 触发 pendingPromise reject，让 await 的 caller 走 catch 分支
 */
export function closeLoginGuide() {
  state.visible = false
  if (pendingReject) {
    pendingReject(new Error('用户取消登录'))
  }
  pendingPromise = null
  pendingResolve = null
  pendingReject = null
  state.stayOnClose = false
  state.stayAfterLogin = false
}

/**
 * 登录成功后调用：关闭弹窗 + resolve pendingPromise
 * 由 LoginGuidePopup 在 userStore.loginAndFetch 成功后调用
 */
export function onLoginSuccess() {
  state.visible = false
  if (pendingResolve) {
    pendingResolve()
  }
  pendingPromise = null
  pendingResolve = null
  pendingReject = null
  state.stayOnClose = false
  state.stayAfterLogin = false
}

/** 组合式入口：返回响应式状态 + 操作方法 */
export function useLoginGuide() {
  return {
    state,
    showLoginGuide,
    closeLoginGuide,
    onLoginSuccess
  }
}

export default useLoginGuide
